/*
 * Matilde — storefront behaviour. No dependencies.
 * Components are wired with data attributes so sections stay plain Liquid.
 */
(() => {
  const theme = window.theme || { routes: {}, strings: {} };
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const debounce = (fn, wait) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  /* ---------------------------------------------------------------------
   * Dialogs (bag, search, menu, quick view, filters)
   * ------------------------------------------------------------------- */
  let returnFocus = null;

  function openDialog(dialog) {
    if (!dialog) return;
    const current = $('dialog[open]');
    if (current === dialog) return;
    if (current) {
      current.dataset.switching = 'true';
      current.close();
    } else {
      returnFocus = document.activeElement;
    }
    dialog.showModal();
    const autofocus = $('[data-autofocus]', dialog);
    if (autofocus) autofocus.focus();
  }

  function closeDialog(dialog) {
    if (dialog && dialog.open) dialog.close();
  }

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement)) return;
      if (dialog.dataset.switching) {
        delete dialog.dataset.switching;
        return;
      }
      setTimeout(() => {
        if (!$('dialog[open]') && returnFocus && returnFocus.isConnected) returnFocus.focus();
      });
    },
    true
  );

  // Click on the backdrop closes the dialog.
  document.addEventListener('click', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    const rect = dialog.getBoundingClientRect();
    const outside =
      event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    if (outside) closeDialog(dialog);
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (dialog) {
        event.preventDefault();
        openDialog(dialog);
      }
      return;
    }
    if (event.target.closest('[data-close]')) {
      closeDialog(event.target.closest('dialog'));
    }
  });

  /* ---------------------------------------------------------------------
   * Toast
   * ------------------------------------------------------------------- */
  let toastTimer;
  function toast(message) {
    const el = $('#Toast');
    if (!el) return;
    el.textContent = message;
    el.classList.add('visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('visible'), 2600);
  }

  /* ---------------------------------------------------------------------
   * Cart
   * ------------------------------------------------------------------- */
  const cartSectionIds = () => {
    const ids = [];
    if ($('#CartDrawer')) ids.push('cart-drawer');
    $$('[data-cart-page]').forEach((el) => ids.push(el.dataset.sectionId));
    return ids;
  };

  function updateCartCount(count) {
    if (count === undefined || count === null) return;
    $$('[data-cart-count-bubble]').forEach((bubble) => {
      bubble.textContent = count;
      bubble.classList.add('is-bumped');
      setTimeout(() => bubble.classList.remove('is-bumped'), 220);
    });
  }

  function renderSections(sections) {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      if (id === 'cart-drawer') {
        const fresh = $('[data-cart-drawer-inner]', doc);
        const current = $('[data-cart-drawer-inner]');
        if (fresh && current) current.replaceWith(fresh);
        if (fresh) updateCartCount(fresh.dataset.cartCount);
        return;
      }
      const target = document.getElementById(`shopify-section-${id}`);
      const source = doc.getElementById(`shopify-section-${id}`);
      if (target && source) {
        target.innerHTML = source.innerHTML;
        const counter = $('[data-cart-count]', target);
        if (counter) updateCartCount(counter.dataset.cartCount);
      }
    });
  }

  async function postJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw data;
    return data;
  }

  async function addToCart(form) {
    const body = new FormData(form);
    const sections = cartSectionIds();
    if (sections.length) {
      body.append('sections', sections.join(','));
      body.append('sections_url', window.location.pathname);
    }
    const response = await fetch(`${theme.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    const data = await response.json();
    if (!response.ok || data.status) throw data;

    if (theme.cartType === 'page' && !$('[data-cart-page]')) {
      window.location.href = theme.routes.cart;
      return;
    }
    renderSections(data.sections);
    const drawer = $('#CartDrawer');
    if (drawer) {
      openDialog(drawer);
    } else {
      closeDialog($('dialog[open]'));
      toast(theme.strings.added);
    }
  }

  async function changeLine(line, quantity) {
    const key = line.dataset.lineKey;
    const error = $('[data-line-error]', line);
    line.classList.add('is-updating');
    try {
      const data = await postJSON(`${theme.routes.cartChange}.js`, {
        id: key,
        quantity,
        sections: cartSectionIds(),
        sections_url: window.location.pathname,
      });
      renderSections(data.sections);
      const updated = data.items && data.items.find((item) => item.key === key);
      if (updated && updated.quantity < quantity) {
        const freshLine = $(`[data-line-key="${CSS.escape(key)}"] [data-line-error]`);
        if (freshLine) {
          freshLine.textContent = theme.strings.cartError;
          freshLine.hidden = false;
        }
      }
    } catch (err) {
      line.classList.remove('is-updating');
      if (error) {
        error.textContent = err.description || err.message || theme.strings.cartError;
        error.hidden = false;
      }
      const input = $('[data-qty-input]', line);
      if (input) input.value = input.defaultValue;
    }
  }

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-step]');
    const remove = event.target.closest('[data-qty-remove]');
    if (!step && !remove) return;
    const line = event.target.closest('[data-line-key]');
    if (!line) return;
    const input = $('[data-qty-input]', line);
    let quantity = 0;
    if (step) {
      quantity = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.qtyStep, 10));
      const max = parseInt(input.max, 10);
      if (max && quantity > max) quantity = max;
      input.value = quantity;
    }
    changeLine(line, quantity);
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-qty-input]')) {
      const line = event.target.closest('[data-line-key]');
      changeLine(line, Math.max(0, parseInt(event.target.value, 10) || 0));
    }
    if (event.target.matches('[data-cart-note]')) {
      postJSON(`${theme.routes.cart}/update.js`, { note: event.target.value }).catch(() => {});
    }
  });

  /* ---------------------------------------------------------------------
   * Product: gallery, variant picker, add to bag
   * ------------------------------------------------------------------- */
  function setThumb(root, index) {
    $$('[data-thumb]', root).forEach((thumb, i) => {
      const active = i === index;
      thumb.classList.toggle('selected', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }

  function showMedia(root, mediaId, instant) {
    const track = $('[data-gallery-track]', root);
    if (!track) return;
    const slides = $$('[data-media-id]', track);
    const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index < 0) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: instant ? 'auto' : 'smooth' });
    setThumb(root, index);
  }

  function initGallery(root) {
    const track = $('[data-gallery-track]', root);
    if (!track || track.dataset.ready) return;
    track.dataset.ready = 'true';
    $$('[data-thumb]', root).forEach((thumb) => {
      thumb.addEventListener('click', () => showMedia(root, thumb.dataset.thumb));
    });
    let frame;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => setThumb(root, Math.round(track.scrollLeft / track.clientWidth)));
      },
      { passive: true }
    );
  }

  function priceHTML(variant) {
    const compare = variant.compare ? `<s class="price-compare">${variant.compare}</s>` : '';
    return `<span class="price${variant.compare ? ' price--sale' : ''}"><span class="price-current">${variant.price}</span>${compare}</span>`;
  }

  function initProduct(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    initGallery(root);

    const jsonEl = $('[data-product-json]', root);
    const data = jsonEl ? JSON.parse(jsonEl.textContent) : { variants: [] };
    const form = $('[data-product-form]', root);
    if (!form) return;
    const idInput = $('[data-variant-id]', form);
    const button = $('[data-add-button]', form);
    const label = $('[data-add-label]', form);
    const error = $('[data-size-error]', root);
    const priceBox = $('[data-price-container]', root);
    const fieldsets = $$('[data-option-index]', root);
    const updateUrl = root.dataset.updateUrl === 'true';

    const selection = () =>
      fieldsets.map((fieldset) => {
        const checked = $('input:checked', fieldset);
        return checked ? checked.value : null;
      });

    function setButton(enabled, text) {
      button.disabled = !enabled;
      label.textContent = text;
    }

    function refreshAvailability(selected) {
      fieldsets.forEach((fieldset, index) => {
        $$('[data-option-value]', fieldset).forEach((input) => {
          const available = data.variants.some(
            (variant) =>
              variant.available &&
              variant.options[index] === input.value &&
              selected.every((value, j) => j === index || value === null || variant.options[j] === value)
          );
          input.closest('.size-option').classList.toggle('is-unavailable', !available);
          if (fieldsets.length === 1) input.disabled = !available;
        });
      });
    }

    function update(fromUser) {
      const selected = selection();
      refreshAvailability(selected);
      if (selected.includes(null)) return;
      if (error) error.textContent = '';
      const variant = data.variants.find((v) => v.options.every((option, i) => option === selected[i]));
      if (!variant) {
        idInput.value = '';
        setButton(false, theme.strings.unavailable);
        return;
      }
      idInput.value = variant.id;
      setButton(variant.available, variant.available ? theme.strings.addToCart : theme.strings.soldOut);
      if (priceBox) priceBox.innerHTML = priceHTML(variant);
      if (variant.media) showMedia(root, variant.media, !fromUser);
      if (updateUrl && fromUser) {
        window.history.replaceState(null, '', `${window.location.pathname}?variant=${variant.id}`);
      }
    }

    fieldsets.forEach((fieldset) => fieldset.addEventListener('change', () => update(true)));
    if (fieldsets.length) update(false);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!idInput.value) {
        if (error) error.textContent = theme.strings.chooseSize;
        const missing = fieldsets.find((fieldset) => !$('input:checked', fieldset));
        const target = missing && $('input:not(:disabled)', missing);
        if (target) target.focus();
        return;
      }
      button.classList.add('is-loading');
      try {
        await addToCart(form);
      } catch (err) {
        if (error) error.textContent = err.description || err.message || theme.strings.cartError;
      } finally {
        button.classList.remove('is-loading');
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Quick view (+ on product cards)
   * ------------------------------------------------------------------- */
  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-quick-view]');
    if (!trigger) return;
    event.preventDefault();
    const dialog = $('#QuickView');
    const content = dialog && $('[data-quick-view-content]', dialog);
    if (!content) return;
    trigger.classList.add('is-loading');
    try {
      const url = trigger.dataset.quickView;
      const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=quick-view`);
      if (!response.ok) throw new Error(response.statusText);
      const root = $('[data-product-root]', parseHTML(await response.text()));
      if (!root) throw new Error('No product');
      content.replaceChildren(root);
      initProduct(root);
      openDialog(dialog);
    } catch (err) {
      window.location.href = trigger.dataset.quickView;
    } finally {
      trigger.classList.remove('is-loading');
    }
  });

  /* ---------------------------------------------------------------------
   * Search dialog + predictive search
   * ------------------------------------------------------------------- */
  function initSearch() {
    const dialog = $('#SearchDialog');
    if (!dialog) return;
    const input = $('[data-predictive-input]', dialog);
    const live = $('[data-search-live]', dialog);
    const fallback = $('[data-search-default]', dialog);

    if (!theme.predictiveSearch || !theme.routes.predictiveSearch) return;
    let controller;
    input.addEventListener(
      'input',
      debounce(async () => {
        const query = input.value.trim();
        if (!query) {
          live.hidden = true;
          fallback.hidden = false;
          return;
        }
        if (controller) controller.abort();
        controller = new AbortController();
        const params = new URLSearchParams({
          q: query,
          'resources[type]': 'product',
          'resources[limit]': '6',
          'resources[options][unavailable_products]': 'last',
          section_id: 'predictive-search',
        });
        try {
          const response = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: controller.signal });
          const results = $('[data-predictive-content]', parseHTML(await response.text()));
          live.replaceChildren(...(results ? [results] : []));
          live.hidden = false;
          fallback.hidden = true;
        } catch (err) {
          if (err.name !== 'AbortError') live.hidden = true;
        }
      }, 220)
    );
  }

  /* ---------------------------------------------------------------------
   * Featured collection with category tabs (homepage)
   * ------------------------------------------------------------------- */
  function initCollectionTabs(section) {
    if (section.dataset.ready) return;
    section.dataset.ready = 'true';
    const tabs = $$('[role="tab"]', section);
    const panels = $$('[role="tabpanel"]', section);
    const more = $('[data-more]', section);
    const sort = $('[data-sort]', section);
    const viewAll = $('[data-view-all]', section);
    const initial = parseInt(section.dataset.initial, 10) || 4;
    let expanded = false;

    const activePanel = () => panels.find((panel) => !panel.hidden) || panels[0];

    function layout() {
      const panel = activePanel();
      if (!panel) return;
      const cards = $$('.product-card', panel);
      cards.forEach((card, i) => {
        card.hidden = !expanded && i >= initial;
      });
      if (more) more.hidden = expanded || cards.length <= initial;
    }

    function applySort() {
      const mode = sort ? sort.value : 'featured';
      panels.forEach((panel) => {
        const cards = $$('.product-card', panel);
        cards.sort((a, b) => {
          if (mode === 'asc') return a.dataset.price - b.dataset.price;
          if (mode === 'desc') return b.dataset.price - a.dataset.price;
          return a.dataset.index - b.dataset.index;
        });
        cards.forEach((card) => panel.appendChild(card));
      });
    }

    function select(tab, focus) {
      tabs.forEach((t) => {
        const active = t === tab;
        t.setAttribute('aria-selected', String(active));
        t.tabIndex = active ? 0 : -1;
        t.classList.toggle('active', active);
      });
      panels.forEach((panel) => {
        panel.hidden = panel.id !== tab.getAttribute('aria-controls');
      });
      $$('[data-tab-note]', section).forEach((note) => {
        note.hidden = note.dataset.tabNote !== tab.id;
      });
      if (viewAll && tab.dataset.url) {
        viewAll.href = tab.dataset.url;
        const count = $('.count', viewAll);
        if (count) count.textContent = tab.dataset.count;
      }
      expanded = false;
      layout();
      if (focus) tab.focus();
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        const next = tabs[(index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
        select(next, true);
      });
    });
    if (more) {
      more.addEventListener('click', () => {
        expanded = true;
        layout();
      });
    }
    if (sort) {
      sort.addEventListener('change', () => {
        applySort();
        layout();
      });
    }
    layout();
  }

  /* ---------------------------------------------------------------------
   * Collection & search pages: sorting, filters, “load more”
   * ------------------------------------------------------------------- */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-sort-select]');
    if (!select) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-facets-form]');
    if (!form) return;
    // Drop empty price inputs so they don't become empty filters.
    $$('input[type="number"]', form).forEach((input) => {
      if (input.value === '') input.disabled = true;
    });
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-load-more]');
    if (!button) return;
    event.preventDefault();
    const grid = document.getElementById(button.dataset.grid);
    if (!grid) return;
    button.classList.add('is-loading');
    try {
      const url = new URL(button.href, window.location.origin);
      url.searchParams.set('section_id', button.dataset.sectionId);
      const doc = parseHTML(await (await fetch(url)).text());
      const freshGrid = doc.getElementById(button.dataset.grid);
      if (freshGrid) grid.append(...freshGrid.children);
      const nextButton = $('[data-load-more]', doc);
      if (nextButton) {
        button.href = nextButton.href;
      } else {
        button.closest('[data-pagination]').remove();
      }
    } catch (err) {
      window.location.href = button.href;
    } finally {
      button.classList.remove('is-loading');
    }
  });

  /* ---------------------------------------------------------------------
   * Product recommendations (loaded after the page)
   * ------------------------------------------------------------------- */
  async function initRecommendations(section) {
    if (section.dataset.ready || !section.dataset.url) return;
    section.dataset.ready = 'true';
    try {
      const doc = parseHTML(await (await fetch(section.dataset.url)).text());
      const fresh = $('[data-recommendations]', doc);
      if (fresh && fresh.innerHTML.trim()) {
        section.innerHTML = fresh.innerHTML;
      } else {
        section.remove();
      }
    } catch (err) {
      section.remove();
    }
  }

  /* ---------------------------------------------------------------------
   * Boot + theme editor support
   * ------------------------------------------------------------------- */
  function init(scope = document) {
    $$('[data-product-root]', scope).forEach(initProduct);
    $$('[data-collection-tabs]', scope).forEach(initCollectionTabs);
    $$('[data-recommendations]', scope).forEach(initRecommendations);
  }

  init();
  initSearch();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    const dialog = $('dialog[open]');
    if (dialog && !event.target.contains(dialog)) closeDialog(dialog);
  });
})();
